import { computed, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { defineLocalStore } from '@/utils/localStore'
import { permissionList, roleSingle, roleUpdate } from '@/requests/role'
import { useStore } from 'vuex'
import { openDialog } from '@/dialog'
import {
  DataScope,
  Permission,
  PermissionGroup,
  PermissionSection,
  Role,
} from '@/utils/types/role'

const dataScopeItems: {
  value: Exclude<DataScope, null>
  title: string
  subtitle: string
}[] = [
  {
    value: 'AUTHOR_ONLY',
    title: 'Тільки свої',
    subtitle: 'Доступ лише до документів, які користувач створив особисто.',
  },
  {
    value: 'PARTICIPATING',
    title: 'Свої та де я учасник',
    subtitle:
      'Свої документи плюс ті, де користувач є учасником (погоджувач, візувальник, ознайомлення тощо).',
  },
  {
    value: 'PARTICIPATING_AND_DEPARTMENT',
    title: 'Свої, де я учасник, і підрозділу',
    subtitle:
      'Свої документи, документи де користувач є учасником, а також усі документи його підрозділу.',
  },
  {
    value: 'ALL',
    title: 'Усі документи в системі',
    subtitle:
      'Повний доступ до всіх документів у системі, незалежно від автора чи підрозділу.',
  },
]

export const { useLocalStore: useRole, initLocalStore: initRole } =
  defineLocalStore('role', () => {
    const route = useRoute()
    const vuex = useStore()

    const roleId = computed<number>(() => Number(route.params.id))

    const role = reactive<Role>({
      name: null,
      description: null,
      data_scope: null,
      guard_name: null as any,
      is_system: false,
      permissions: [],
    })
    const groupedPermissions = ref<Record<string, Permission[]>>({})
    const groupLabels = ref<Record<string, string>>({})
    const permissionSections = ref<PermissionSection[]>([])
    const selectedPermissions = ref<string[]>([])

    const normalizeSections = (
      groups: Record<string, Permission[]> = {},
      labels: Record<string, string> = {},
      sections: PermissionSection[] = []
    ) => {
      if (sections.length) {
        return sections
      }

      return Object.entries(groups).map(([key, permissions]) => ({
        label: labels[key] || key,
        groups: [
          {
            key,
            label: labels[key] || key,
            permissions,
          },
        ],
      }))
    }

    const allPermissions = computed(() => {
      return permissionSections.value.flatMap(section =>
        section.groups.flatMap(group => group.permissions)
      )
    })

    const allPermissionNames = computed(() => {
      return allPermissions.value.map(p => p.name)
    })

    const getGroupPermissions = (groupKey: string) => {
      return getGroupByKey(groupKey)?.permissions || []
    }

    const getGroupByKey = (groupKey: string) => {
      return permissionSections.value
        .flatMap(section => section.groups)
        .find(item => item.key === groupKey)
    }

    const getSectionPermissions = (section: PermissionSection) => {
      return section.groups.flatMap(group => group.permissions)
    }

    const getPermissionNames = (permissions: Permission[]) => {
      return permissions.map(permission => permission.name)
    }

    const getSelectedCount = (permissions: Permission[]) => {
      return permissions.filter(p => selectedPermissions.value.includes(p.name))
        .length
    }

    const arePermissionsSelected = (permissions: Permission[]) => {
      return (
        permissions.length > 0 &&
        permissions.every(p => selectedPermissions.value.includes(p.name))
      )
    }

    const arePermissionsIndeterminate = (permissions: Permission[]) => {
      const selected = getSelectedCount(permissions)

      return selected > 0 && selected < permissions.length
    }

    const togglePermissions = (permissions: Permission[], val: boolean) => {
      const names = getPermissionNames(permissions)

      if (val) {
        selectedPermissions.value = [
          ...new Set([...selectedPermissions.value, ...names]),
        ]
      } else {
        selectedPermissions.value = selectedPermissions.value.filter(
          p => !names.includes(p)
        )
      }
    }

    const getActionLabel = (perm: Permission) => {
      return perm.description || perm.name.split('.')[1]
    }

    const isGroupSelected = (group: string) => {
      return arePermissionsSelected(getGroupPermissions(group))
    }

    const getGroupSelectedCount = (group: string) => {
      return getSelectedCount(getGroupPermissions(group))
    }

    const isGroupIndeterminate = (group: string) => {
      return arePermissionsIndeterminate(getGroupPermissions(group))
    }

    const isSectionSelected = (section: PermissionSection) => {
      return arePermissionsSelected(getSectionPermissions(section))
    }

    const getSectionSelectedCount = (section: PermissionSection) => {
      return getSelectedCount(getSectionPermissions(section))
    }

    const getSectionPermissionsCount = (section: PermissionSection) => {
      return getSectionPermissions(section).length
    }

    const isSectionIndeterminate = (section: PermissionSection) => {
      return arePermissionsIndeterminate(getSectionPermissions(section))
    }

    const toggleSection = (section: PermissionSection, val: boolean) => {
      togglePermissions(getSectionPermissions(section), val)
    }

    const toggleGroup = (group: string | PermissionGroup, val: boolean) => {
      const permissions =
        typeof group === 'string'
          ? getGroupPermissions(group)
          : group.permissions || []

      togglePermissions(permissions, val)
    }

    const togglePermission = (permission: string, val: boolean) => {
      if (val) {
        selectedPermissions.value = [
          ...new Set([...selectedPermissions.value, permission]),
        ]
      } else {
        selectedPermissions.value = selectedPermissions.value.filter(
          item => item !== permission
        )
      }
    }

    const selectAll = () => {
      selectedPermissions.value = [...allPermissionNames.value]
    }

    const deselectAll = () => {
      selectedPermissions.value = []
    }

    const setRole = (obj: Role) => {
      Object.assign(role, obj)

      if (obj.permissions) {
        selectedPermissions.value = obj.permissions.map(p => p.name)
      }
    }

    const openRoleDialog = () => {
      openDialog({
        name: 'Role',
        action: 'update',
        params: {
          title: 'Редагування ролі',
          submitLabel: 'Зберегти',
          cb: (res: Role) => {
            setRole(res)
          },
        },
        dialogData: {
          roleData: role,
        },
      })
    }

    const getRoleSingle = () => {
      return roleSingle(roleId.value).then(res => {
        setRole(res)
      })
    }

    const getPermissions = () => {
      return permissionList().then(res => {
        groupedPermissions.value = res.groups || {}
        groupLabels.value = res.groupLabels || {}
        permissionSections.value = normalizeSections(
          groupedPermissions.value,
          groupLabels.value,
          res.sections || []
        )
      })
    }

    const submit = () => {
      return roleUpdate(roleId.value, {
        name: role.name,
        description: role.description,
        data_scope: role.data_scope,
        permissions: selectedPermissions.value,
      }).then(res => {
        setRole(res)

        vuex.dispatch('user/getUserData')
      })
    }

    getRoleSingle()
    getPermissions()

    return {
      role,
      dataScopeItems,
      groupedPermissions,
      groupLabels,
      permissionSections,
      selectedPermissions,

      getActionLabel,
      isGroupSelected,
      isGroupIndeterminate,
      getGroupSelectedCount,
      isSectionSelected,
      isSectionIndeterminate,
      getSectionSelectedCount,
      getSectionPermissionsCount,
      toggleGroup,
      toggleSection,
      togglePermission,
      selectAll,
      deselectAll,
      openRoleDialog,
      submit,
    }
  })
